import { useState, useEffect, useRef } from "react";
import { 
  User, 
  Camera, 
  Save, 
  Loader2, 
  Briefcase, 
  MapPin, 
  Phone,
  Instagram,
  Twitter,
  Globe,
  Trash2,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const departments = [
  { value: "production", label: "Production" },
  { value: "talent", label: "Talent / Modeling" },
  { value: "marketing", label: "Marketing" },
  { value: "creative", label: "Creative" },
  { value: "operations", label: "Operations" },
  { value: "management", label: "Management" },
  { value: "other", label: "Other" }
];

const Profile = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profile, setProfile] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    bio: "",
    department: "",
    phone: "",
    location: "",
    social_links: {
      instagram: "",
      twitter: "",
      website: ""
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/profile`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          name: data.name || "",
          title: data.title || "",
          bio: data.bio || "",
          department: data.department || "",
          phone: data.phone || "",
          location: data.location || "",
          social_links: data.social_links || { instagram: "", twitter: "", website: "" }
        });
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        if (setUser && data.profile.name) {
          setUser(prev => ({ ...prev, name: data.profile.name }));
        }
        toast.success("Profile updated successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePictureSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/profile/picture`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, picture: data.picture }));
        if (setUser) {
          setUser(prev => ({ ...prev, picture: data.picture }));
        }
        toast.success("Profile picture updated!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload picture");
      }
    } catch (error) {
      toast.error("Failed to upload picture");
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePicture = async () => {
    if (!window.confirm("Remove your profile picture?")) return;

    try {
      const response = await fetch(`${API}/profile/picture`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setProfile(prev => ({ ...prev, picture: null }));
        if (setUser) {
          setUser(prev => ({ ...prev, picture: null }));
        }
        toast.success("Profile picture removed");
      }
    } catch (error) {
      toast.error("Failed to remove picture");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif', textShadow: '0 0 20px rgba(255, 215, 0, 0.2)' }}>
          My Profile
        </h1>
        <p className="text-[#E5C558] mt-1">Manage your personal information and settings</p>
      </div>

      {/* Profile Picture Card */}
      <Card 
        style={{
          background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 215, 0, 0.2)',
          boxShadow: '0 0 30px rgba(255, 200, 100, 0.08)'
        }}
      >
        <CardHeader>
          <CardTitle className="text-lg text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Camera className="text-[#FFD700]" size={20} />
            Profile Picture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Picture Preview */}
            <div className="relative">
              {profile?.picture ? (
                <img 
                  src={profile.picture} 
                  alt={profile.name}
                  className="w-24 h-24 rounded-full object-cover"
                  style={{ border: '3px solid #FFD700', boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)' }}
                />
              ) : (
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold"
                  style={{ 
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    color: '#0a0500',
                    boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)'
                  }}
                >
                  {profile?.name?.charAt(0) || "U"}
                </div>
              )}
              {uploadingPicture && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <Loader2 className="animate-spin text-[#FFD700]" size={24} />
                </div>
              )}
            </div>

            {/* Upload Controls */}
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePictureSelect}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                className="bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
              >
                <Camera size={16} className="mr-2" />
                Upload New Picture
              </Button>
              {profile?.picture && (
                <Button
                  variant="outline"
                  onClick={handleRemovePicture}
                  className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                >
                  <Trash2 size={16} className="mr-2" />
                  Remove
                </Button>
              )}
              <p className="text-xs text-[#71717A]">JPEG, PNG, GIF, WebP • Max 5MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Info Card */}
      <Card 
        style={{
          background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 215, 0, 0.2)',
          boxShadow: '0 0 30px rgba(255, 200, 100, 0.08)'
        }}
      >
        <CardHeader>
          <CardTitle className="text-lg text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <User className="text-[#FFD700]" size={20} />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <Label className="text-[#E5C558]">Display Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                className="text-[#FFFFFF]"
                style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
              />
            </div>

            {/* Title */}
            <div>
              <Label className="text-[#E5C558]">Title at DopeInHD</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Lead Photographer, Senior Model"
                className="text-[#FFFFFF]"
                style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
              />
            </div>

            {/* Department */}
            <div>
              <Label className="text-[#E5C558]">Department</Label>
              <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                <SelectTrigger className="text-[#FFFFFF]" style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  {departments.map((dept) => (
                    <SelectItem key={dept.value} value={dept.value} className="text-[#FFFFFF]">
                      {dept.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phone */}
            <div>
              <Label className="text-[#E5C558]">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="pl-10 text-[#FFFFFF]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                />
              </div>
            </div>

            {/* Location */}
            <div className="md:col-span-2">
              <Label className="text-[#E5C558]">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="City, State"
                  className="pl-10 text-[#FFFFFF]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <Label className="text-[#E5C558]">Bio / Description</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell us about yourself, your role, expertise, and what you do at DopeInHD..."
              rows={4}
              className="text-[#FFFFFF]"
              style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
            />
            <p className="text-xs text-[#71717A] mt-1">{formData.bio?.length || 0}/500 characters</p>
          </div>
        </CardContent>
      </Card>

      {/* Social Links Card */}
      <Card 
        style={{
          background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 215, 0, 0.2)',
          boxShadow: '0 0 30px rgba(255, 200, 100, 0.08)'
        }}
      >
        <CardHeader>
          <CardTitle className="text-lg text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Globe className="text-[#FFD700]" size={20} />
            Social Links
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Instagram */}
            <div>
              <Label className="text-[#E5C558]">Instagram</Label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E1306C]" size={16} />
                <Input
                  value={formData.social_links?.instagram || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, instagram: e.target.value }
                  })}
                  placeholder="@username"
                  className="pl-10 text-[#FFFFFF]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                />
              </div>
            </div>

            {/* Twitter */}
            <div>
              <Label className="text-[#E5C558]">Twitter / X</Label>
              <div className="relative">
                <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1DA1F2]" size={16} />
                <Input
                  value={formData.social_links?.twitter || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, twitter: e.target.value }
                  })}
                  placeholder="@username"
                  className="pl-10 text-[#FFFFFF]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                />
              </div>
            </div>

            {/* Website */}
            <div className="md:col-span-2">
              <Label className="text-[#E5C558]">Personal Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                <Input
                  value={formData.social_links?.website || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, website: e.target.value }
                  })}
                  placeholder="https://yourwebsite.com"
                  className="pl-10 text-[#FFFFFF]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info (Read-only) */}
      <Card 
        style={{
          background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 215, 0, 0.15)',
        }}
      >
        <CardHeader>
          <CardTitle className="text-lg text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Briefcase className="text-[#FFD700]" size={20} />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.5)' }}>
              <p className="text-xs text-[#71717A]">Email</p>
              <p className="text-[#FFFFFF] font-medium">{profile?.email}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.5)' }}>
              <p className="text-xs text-[#71717A]">Role</p>
              <p className="text-[#FFD700] font-medium capitalize">{profile?.role?.replace('_', ' ')}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.5)' }}>
              <p className="text-xs text-[#71717A]">Status</p>
              <p className="text-[#22C55E] font-medium capitalize flex items-center gap-2">
                <CheckCircle size={14} />
                {profile?.status}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveProfile}
          disabled={saving}
          className="bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold px-8"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin mr-2" size={18} />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Profile;
